import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { BACKEND_URL as API } from '../../apiConfig';
import { getInternalFleetCopy } from '../../pages/internal/internalFleetI18n';

const FleetFinances = ({ canEdit = true, language = 'zh' }) => {
  const copy = getInternalFleetCopy(language);
  const t = copy.finances;
  const common = copy.common;
  const [vehicles, setVehicles] = useState([]);
  const [financeData, setFinanceData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedVehicle, setExpandedVehicle] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState('income'); // income, expense, unpaid
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);

  const initialForm = {
    item_type: 'income',
    category: 'other',
    item_name: '',  // 非必填
    amount: '',
    currency: 'JPY',
    is_paid: true,
    notes: '',
  };

  const [form, setForm] = useState(initialForm);

  const incomeCategories = [
    { value: 'deposit', label: '押金' },
    { value: 'license_fee', label: '牌照使用费' },
    { value: 'insurance', label: '保险费' },
    { value: 'other', label: '其他收入' },
  ];

  const expenseCategories = [
    { value: 'insurance', label: '保险费' },
    { value: 'maintenance', label: '维修费' },
    { value: 'fuel', label: '油费' },
    { value: 'other', label: '其他支出' },
  ];

  const currencies = [
    { value: 'JPY', label: '日元 (¥)' },
    { value: 'CNY', label: '人民币 (¥)' },
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    try {
      const vehiclesRes = await axios.get(`${API}/api/internal/vehicles`);
      const vehiclesList = vehiclesRes.data || [];
      setVehicles(vehiclesList);

      // 获取每辆车的财务数据
      const financePromises = vehiclesList.map(v => 
        axios.get(`${API}/api/internal/finances/vehicle/${v.id}`)
          .then(res => ({ vehicleId: v.id, data: res.data }))
          .catch(() => ({ vehicleId: v.id, data: { items: [], summary: {} } }))
      );
      
      const financeResults = await Promise.all(financePromises);
      const financeMap = {};
      financeResults.forEach(r => {
        financeMap[r.vehicleId] = r.data;
      });
      setFinanceData(financeMap);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = (vehicleId, type) => {
    setSelectedVehicleId(vehicleId);
    setModalType(type);
    setForm({
      ...initialForm,
      item_type: type === 'unpaid' ? 'income' : type,
      is_paid: type !== 'unpaid',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        vehicle_id: selectedVehicleId,
        amount: parseFloat(form.amount),
      };

      await axios.post(`${API}/api/internal/finances/items`, payload);
      
      fetchAllData();
      setShowModal(false);
      setForm(initialForm);
    } catch (error) {
      console.error('Failed to save finance item:', error);
      alert('保存失败: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSoftDelete = async (itemId) => {
    if (window.confirm('确定要删除这条记录吗？将保留删除记录。')) {
      try {
        await axios.put(`${API}/api/internal/finances/items/${itemId}`, {
          is_deleted: true,
        });
        fetchAllData();
      } catch (error) {
        console.error('Failed to delete item:', error);
        alert('删除失败');
      }
    }
  };

  const handleHardDelete = async (itemId) => {
    if (window.confirm('确定要永久删除这条记录吗？')) {
      try {
        await axios.delete(`${API}/api/internal/finances/items/${itemId}`);
        fetchAllData();
      } catch (error) {
        console.error('Failed to delete item:', error);
        alert('删除失败');
      }
    }
  };

  const handleMarkPaid = async (itemId) => {
    try {
      await axios.put(`${API}/api/internal/finances/items/${itemId}/mark-paid`);
      fetchAllData();
    } catch (error) {
      console.error('Failed to mark as paid:', error);
      alert('操作失败');
    }
  };

  const formatCurrency = (amount, currency = 'JPY') => {
    return `¥${(amount || 0).toLocaleString()}`;
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleString('zh-CN', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const getVehicleFinance = (vehicleId) => {
    return financeData[vehicleId] || { items: [], summary: {} };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  if (vehicles.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">🚗</div>
        <h3 className="text-white text-xl font-bold mb-2">{t.noVehiclesTitle}</h3>
        <p className="text-slate-400">{t.noVehiclesHint}</p>
      </div>
    );
  }

  return (
    <div className="internal-page-skin space-y-4">
      {/* 车辆财务列表 */}
      {vehicles.map((vehicle) => {
        const finance = getVehicleFinance(vehicle.id);
        const summary = finance.summary || {};
        const items = finance.items || [];
        const isExpanded = expandedVehicle === vehicle.id;

        const incomeItems = items.filter(i => i.item_type === 'income' && !i.is_deleted);
        const deletedItems = items.filter(i => i.is_deleted);
        const expenseItems = items.filter(i => i.item_type === 'expense' && !i.is_deleted);
        const unpaidItems = incomeItems.filter(i => !i.is_paid);

        // 计算此车辆的差额
        const vehicleBalance = (summary.deposit_amount || 0) > 0 
          ? (summary.deposit_amount || 0) - (summary.unpaid_amount || 0)
          : -(summary.unpaid_amount || 0);

        return (
          <div key={vehicle.id} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
            {/* 车辆标题栏 */}
            <div 
              className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-700/30 transition-colors"
              onClick={() => setExpandedVehicle(isExpanded ? null : vehicle.id)}
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center text-2xl">
                  🚗
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">{vehicle.plate_number}</h3>
                  <p className="text-slate-400 text-sm">
                    {vehicle.vehicle_type === 'alphard' ? 'Alphard' : 
                     vehicle.vehicle_type === 'hiace' ? 'Hiace' : vehicle.vehicle_type_other || vehicle.vehicle_type}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-6">
                {/* 未收款数量 */}
                {unpaidItems.length > 0 && (
                  <div className="bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm font-bold">
                    {unpaidItems.length} {t.unpaidCount}
                  </div>
                )}

                <div className="text-right">
                  <p className="text-slate-400 text-xs">{t.income}</p>
                  <p className="text-emerald-400 font-bold">{formatCurrency(summary.total_income)}</p>
                </div>
                <div className="text-right">
                  <p className="text-slate-400 text-xs">{t.expense}</p>
                  <p className="text-red-400 font-bold">{formatCurrency(summary.total_expense)}</p>
                </div>
                <span className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}>
                  ▼
                </span>
              </div>
            </div>

            {/* 展开详情 */}
            {isExpanded && (
              <div className="border-t border-slate-700 p-4">
                {/* 此车辆的汇总信息 */}
                <div className="grid grid-cols-3 gap-4 mb-4">
                  {/* 差额（押金-未收款） */}
                  <div className={`rounded-lg p-4 ${
                    vehicleBalance >= 0 
                      ? 'bg-emerald-500/10 border border-emerald-500/30' 
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}>
                    <p className="text-slate-400 text-xs">{t.balance}</p>
                    <p className={`text-xl font-bold ${
                      vehicleBalance >= 0 ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {vehicleBalance >= 0 ? '+' : ''}{formatCurrency(vehicleBalance)}
                    </p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-xs">{t.totalIncome}</p>
                    <p className="text-emerald-400 text-xl font-bold">{formatCurrency(summary.total_income)}</p>
                  </div>
                  <div className="bg-slate-700/50 rounded-lg p-4">
                    <p className="text-slate-400 text-xs">{t.totalExpense}</p>
                    <p className="text-red-400 text-xl font-bold">{formatCurrency(summary.total_expense)}</p>
                  </div>
                </div>

                {/* 操作按钮 */}
                {/* 操作按钮 - 只有编辑权限才显示 */}
                {canEdit && (
                  <div className="flex gap-3 mb-4">
                    <button
                      onClick={() => openAddModal(vehicle.id, 'income')}
                      className="bg-emerald-500/20 text-emerald-400 px-4 py-2 rounded-lg hover:bg-emerald-500/30 transition-colors font-medium"
                    >
                      + {t.addIncome}
                    </button>
                    <button
                      onClick={() => openAddModal(vehicle.id, 'expense')}
                      className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg hover:bg-red-500/30 transition-colors font-medium"
                    >
                      + {t.addExpense}
                    </button>
                    <button
                      onClick={() => openAddModal(vehicle.id, 'unpaid')}
                      className="bg-amber-500/20 text-amber-400 px-4 py-2 rounded-lg hover:bg-amber-500/30 transition-colors font-medium"
                    >
                      + {t.addUnpaid}
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* 收入列表 */}
                  <div className="bg-slate-700/30 rounded-lg p-4">
                    <h4 className="text-emerald-400 font-bold mb-3 flex items-center gap-2">
                      💰 {t.incomeItems}
                      <span className="text-slate-400 text-sm font-normal">({incomeItems.length}{t.records})</span>
                    </h4>
                    <div className="space-y-2">
                      {incomeItems.map((item) => (
                        <div key={item.id} className="p-3 bg-slate-700/50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`font-medium ${!item.is_paid ? 'text-red-400 font-bold' : 'text-white'}`}>
                                  {item.item_name || incomeCategories.find(c => c.value === item.category)?.label || '未命名'}
                                </span>
                                {!item.is_paid && (
                                  <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded font-bold">
                                    {t.unpaid}
                                  </span>
                                )}
                              </div>
                              <p className="text-slate-500 text-xs mt-1">
                                {incomeCategories.find(c => c.value === item.category)?.label}
                                {item.notes && ` · ${item.notes}`}
                              </p>
                              {/* 显示创建人和时间 */}
                              <p className="text-slate-600 text-xs mt-1">
                                  {language === 'ja' ? '作成' : '创建'}: {item.created_by_name || (language === 'ja' ? '不明' : '未知')} · {formatDateTime(item.created_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className={`font-bold ${!item.is_paid ? 'text-red-400' : 'text-emerald-400'}`}>
                                +{formatCurrency(item.amount, item.currency)}
                              </span>
                              {canEdit && !item.is_paid && (
                                <button
                                  onClick={() => handleMarkPaid(item.id)}
                                  className="bg-emerald-500 text-white px-2 py-1 rounded text-xs hover:bg-emerald-600"
                                  title={t.paid}
                                >
                                  ✓ {t.paid}
                                </button>
                              )}
                              {canEdit && (
                                <button
                                  onClick={() => handleSoftDelete(item.id)}
                                  className="text-red-400 hover:text-red-300 text-sm"
                                  title={language === 'ja' ? '削除（記録保持）' : '删除（保留记录）'}
                                >
                                  {common.delete}
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      {incomeItems.length === 0 && (
                        <p className="text-slate-500 text-center py-4">{t.emptyIncome}</p>
                      )}
                    </div>

                    {/* 已删除的收入记录 */}
                    {deletedItems.filter(i => i.item_type === 'income').length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-600">
                        <h5 className="text-slate-500 text-sm mb-2">{t.deletedRecords}</h5>
                        {deletedItems.filter(i => i.item_type === 'income').map((item) => (
                          <div key={item.id} className="p-2 bg-slate-800/50 rounded mb-2 opacity-60">
                            <div className="flex items-center justify-between">
                              <span className="text-slate-400 line-through">
                                {item.item_name || incomeCategories.find(c => c.value === item.category)?.label}
                              </span>
                              <span className="text-slate-500 line-through">{formatCurrency(item.amount)}</span>
                            </div>
                            <p className="text-red-400 text-xs mt-1">
                              删除人: {item.deleted_by_name || '未知'} · {formatDateTime(item.deleted_at)}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* 支出列表 */}
                  <div className="bg-slate-700/30 rounded-lg p-4">
                    <h4 className="text-red-400 font-bold mb-3 flex items-center gap-2">
                      📉 {t.expenseItems}
                      <span className="text-slate-400 text-sm font-normal">({expenseItems.length}{t.records})</span>
                    </h4>
                    <div className="space-y-2">
                      {expenseItems.map((item) => (
                        <div key={item.id} className="p-3 bg-slate-700/50 rounded-lg">
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <span className="text-white font-medium">
                                {item.item_name || expenseCategories.find(c => c.value === item.category)?.label || '未命名'}
                              </span>
                              <p className="text-slate-500 text-xs mt-1">
                                {expenseCategories.find(c => c.value === item.category)?.label}
                                {item.notes && ` · ${item.notes}`}
                              </p>
                              <p className="text-slate-600 text-xs mt-1">
                                {language === 'ja' ? '作成' : '创建'}: {item.created_by_name || (language === 'ja' ? '不明' : '未知')} · {formatDateTime(item.created_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="text-red-400 font-bold">
                                -{formatCurrency(item.amount, item.currency)}
                              </span>
                              <button
                                onClick={() => handleHardDelete(item.id)}
                                className="text-red-400 hover:text-red-300 text-sm"
                              >
                                {common.delete}
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {expenseItems.length === 0 && (
                        <p className="text-slate-500 text-center py-4">{t.emptyExpense}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-800 rounded-xl p-6 w-full max-w-md border border-slate-700">
            <h3 className="text-white text-xl font-bold mb-6">
              {modalType === 'income' ? t.addIncomeTitle : 
               modalType === 'expense' ? t.addExpenseTitle : t.addUnpaidTitle}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-slate-400 text-sm mb-1">{t.category} *</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                >
                  {(modalType === 'expense' ? expenseCategories : incomeCategories).map(c => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1">{t.itemName} <span className="text-slate-500">({t.optional})</span></label>
                <input
                  type="text"
                  value={form.item_name}
                  onChange={(e) => setForm({ ...form, item_name: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  placeholder="例：2024年保险费"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                <label className="block text-slate-400 text-sm mb-1">{t.amount} *</label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                    placeholder="0"
                    required
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-sm mb-1">{t.currency} *</label>
                  <select
                    value={form.currency}
                    onChange={(e) => setForm({ ...form, currency: e.target.value })}
                    className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  >
                    {currencies.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {modalType === 'unpaid' && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                  <p className="text-amber-400 text-sm">
                    {language === 'ja' ? 'この記録は「未収」として赤色で強調表示されます' : '此记录将被标记为「未支付」，显示为红色粗体'}
                  </p>
                </div>
              )}

              <div>
                <label className="block text-slate-400 text-sm mb-1">{t.notes} <span className="text-slate-500">({t.optional})</span></label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-slate-700 border border-slate-600 text-white px-4 py-2 rounded-lg"
                  rows={2}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setForm(initialForm); }}
                  className="px-6 py-2 text-slate-400 hover:text-white transition-colors"
                >
                  {common.cancel}
                </button>
                <button
                  type="submit"
                  className={`px-6 py-2 rounded-lg font-medium transition-all ${
                    modalType === 'income' ? 'bg-emerald-500 hover:bg-emerald-600 text-white' :
                    modalType === 'expense' ? 'bg-red-500 hover:bg-red-600 text-white' :
                    'bg-amber-500 hover:bg-amber-600 text-white'
                  }`}
                >
                  {common.save}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetFinances;
